import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { setPageMeta } from "@/lib/seo";
import Mascot from "@/components/Mascot";

const FLOW = [
  { path: "/pricing", name: "Tickets" },
  { path: "/menu", name: "Menu" },
  { path: "/facilities", name: "Facilities" },
  { path: "/events", name: "Events" },
  { path: "/times", name: "Times" },
  { path: "/visit", name: "FAQ" },
  { path: "/gallery", name: "Gallery" },
  { path: "/reviews", name: "Reviews" },
  { path: "/contact", name: "Contact" },
];

const VISITED_KEY = "cr-visited";

function readVisited() {
  try {
    return JSON.parse(sessionStorage.getItem(VISITED_KEY) || "[]");
  } catch {
    return [];
  }
}

function PageNavStrip({ pageName }) {
  const navigate = useNavigate();
  const { pathname, key } = useLocation();

  useEffect(() => {
    try {
      const seen = readVisited();
      if (!seen.includes(pathname)) {
        seen.push(pathname);
        sessionStorage.setItem(VISITED_KEY, JSON.stringify(seen));
      }
    } catch {}
  }, [pathname, key]);

  const idx = FLOW.findIndex((p) => p.path === pathname);
  const next = idx >= 0 && idx < FLOW.length - 1 ? FLOW[idx + 1] : null;
  // "Next" only shows for pages the visitor has already been to this visit
  const showNext = !!next && readVisited().includes(next.path);
  const showBack = window.history.length > 1 && key !== "default";

  return (
    <div className="flex items-center justify-between gap-4" data-testid="page-nav-strip">
      <div className="flex min-w-0 items-center gap-3">
        {showBack && (
          <button
            onClick={() => navigate(-1)}
            className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/20 px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[#faf5ea] transition-colors hover:border-[#f8b818] hover:text-[#f8b818]"
            data-testid="page-back-btn"
          >
            <ArrowLeft size={13} strokeWidth={3} /> Back
          </button>
        )}
        <span
          className="truncate text-[11px] font-extrabold uppercase tracking-widest text-[#faf5ea]/70"
          data-testid="page-nav-name"
        >
          {pageName}
        </span>
      </div>
      {showNext && (
        <button
          onClick={() => navigate(next.path)}
          className="flex shrink-0 items-center gap-1.5 rounded-full bg-[#f8b818] px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[#0a0a0b] transition-transform hover:scale-105"
          data-testid="page-next-btn"
        >
          Next: {next.name} <ArrowRight size={13} strokeWidth={3} />
        </button>
      )}
    </div>
  );
}

export default function PageHeader({ pageName, eyebrow, title, subline, image, action, mascot, goodgirlTitle, tight }) {
  useEffect(() => {
    setPageMeta({
      title: `${pageName || title} — Cool Runnings Cape Town`,
      description: subline,
      image,
    });
  }, [pageName, title, subline, image]);

  return (
    <header
      className={`relative flex min-h-[380px] items-end overflow-hidden border-b border-white/10 bg-[#0a0a0b] pb-14 ${tight ? "pt-20 sm:min-h-[380px] sm:pb-16 sm:pt-20" : "pt-36 sm:min-h-[460px] sm:pb-20 sm:pt-44"}`}
      data-testid="page-header"
    >
      <img
        src={image || "/park-track.jpg"}
        alt=""
        aria-hidden="true"
        className="ken absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/60 to-[#0a0a0b]/35" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b]/75 via-[#0a0a0b]/25 to-transparent" />
      {mascot && <Mascot side="right" className="bottom-6 sm:bottom-10" phrases={mascot} greet />}
      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="mb-9"
        >
          <PageNavStrip pageName={pageName} />
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="eyebrow !text-[#f9c92e]"
          data-testid="page-header-eyebrow"
        >
          {eyebrow}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.08 }}
          className={`mt-3 text-4xl sm:text-6xl ${
            goodgirlTitle
              ? "leading-tight text-[#f8b818] drop-shadow-[0_2px_0_rgba(122,86,0,0.7)]"
              : "font-display text-[#faf5ea]"
          }`}
          style={goodgirlTitle ? { fontFamily: '"Goodgirl", "Outfit", sans-serif' } : undefined}
          data-testid="page-header-title"
        >
          {title}
        </motion.h1>
        {subline && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.16 }}
            className="mt-4 max-w-2xl text-sm font-semibold leading-relaxed text-[#faf5ea]/85 sm:text-base"
            data-testid="page-header-subline"
          >
            {subline}
          </motion.p>
        )}
        {action && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.24 }}
            className="mt-7"
          >
            {action}
          </motion.div>
        )}
      </div>
    </header>
  );
}
